import { useEffect, useMemo, useState } from "react";
import { serverFetch } from "@server";
import { Button, Checkbox, message, Popconfirm, Space, Spin, Switch, Table } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import { tokenVids } from "@extensions/enums";
import { useViewportBreakpoint } from "../../hooks/useViewportBreakpoint";
import "../company/company.css";
import "./messages-tokens.css";
import MessagesToken from "./messages-token";

const vidLabel = value => tokenVids.find(t => t.value === value)?.label ?? "—";

const LS_ONLY_ACTIVE_KEY = "messagesTokens.onlyActive";

const MessagesTokens = () => {
  const isMobile = useViewportBreakpoint() === "mobile";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [providers, setProviders] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [onlyActive, setOnlyActive] = useState(() => {
    try {
      return localStorage.getItem(LS_ONLY_ACTIVE_KEY) === "1";
    } catch {
      return false;
    }
  });

  const getData = () => {
    setLoading(true);
    serverFetch("token?internalOnly=false")
      .then(data => {
        setLoading(false);
        setItems(data);
      })
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage || "Ошибка загрузки");
      });
  };

  const getProviders = () => {
    serverFetch("token/Providers")
      .then(data => setProviders(data))
      .catch(e => message.error(e.userMessage || "Ошибка загрузки провайдеров"));
  };

  const getSpaces = () => {
    serverFetch("Cyber/spaces")
      .then(data => setSpaces(Array.isArray(data) ? data : []))
      .catch(() => setSpaces([]));
  };

  useEffect(() => {
    getData();
    getProviders();
    getSpaces();
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(LS_ONLY_ACTIVE_KEY, onlyActive ? "1" : "0");
    } catch {
      // noop
    }
  }, [onlyActive]);

  const filteredItems = useMemo(
    () => (onlyActive ? items.filter(x => !!x.active) : items),
    [items, onlyActive]
  );

  const handleAdd = () => {
    setSelected(0);
  };

  const handleDelete = tokenId => {
    setLoading(true);
    serverFetch(`token/${tokenId}`, { method: "DELETE" })
      .then(() => {
        message.success("Токен удален");
        getData();
      })
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage || "Ошибка при удалении");
      });
  };

  const handleToggleActive = (record, checked) => {
    setLoading(true);
    serverFetch("token", {
      method: "PUT",
      bodyData: { ...record, active: checked },
    })
      .then(() => {
        message.success("Сохранено");
        getData();
      })
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage || "Ошибка сохранения");
      });
  };

  const handleSuccess = () => {
    message.success(selected === 0 ? "Канал добавлен" : "Канал обновлен");
    getData();
  };

  const providerLabel = value =>
    value != null && value !== "" ? providers.find(p => p.id === value)?.name || value : "—";

  const columns = [
    {
      key: "active",
      title: "Акт.",
      width: 64,
      align: "center",
      render: (_, record) => (
        <Checkbox
          checked={!!record.active}
          onChange={e => handleToggleActive(record, e.target.checked)}
          disabled={loading}
        />
      ),
    },
    {
      title: "",
      width: 68,
      render: (_, record) => (
        <div className="actions-on-hover" style={{ display: "flex", gap: 4 }}>
          <Button type="text" size="small" icon={<EditOutlined />} onClick={() => setSelected(record.id)} />
          <Popconfirm
            title="Подтверждение удаления"
            description="Вы уверены, что хотите удалить этот канал?"
            onConfirm={() => handleDelete(record.id)}
            okText="Удалить"
            cancelText="Отмена"
          >
            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </div>
      ),
    },
    { dataIndex: "id", title: "#", width: 56 },
    { dataIndex: "idx", title: "Пор.", width: 72 },
    {
      dataIndex: "name",
      title: "Наименование",
      ellipsis: true,
      render: text => text ?? "—",
    },
    {
      dataIndex: "code",
      title: "Код",
      ellipsis: true,
      render: text => (text != null && String(text) !== "" ? String(text) : "—"),
    },
    { dataIndex: "vid", title: "Вид", width: 100, ellipsis: true, render: value => vidLabel(value) },
    {
      dataIndex: "spaceId",
      title: "Киберменеджер",
      width: 110,
      ellipsis: true,
      render: value => (value != null ? spaces.find(s => s.id === value)?.name ?? value : "—"),
    },
    {
      dataIndex: "provider",
      title: "Провайдер",
      width: 180,
      ellipsis: true,
      render: value => providerLabel(value),
    },
  ];

  const tableEl = (
    <div className="messages-tokens-table-wrap">
      <Table
        className="messages-tokens-desktop-table"
        columns={columns}
        dataSource={filteredItems}
        loading={loading}
        rowKey="id"
        size="small"
        tableLayout="fixed"
        pagination={false}
      />
    </div>
  );

  const mobileListEl = (
    <Spin spinning={loading}>
      <div className="messages-tokens-mobile-list">
        {filteredItems.map(record => {
          const hasName = record.name != null && String(record.name).trim() !== "";
          const codeStr = record.code != null ? String(record.code) : "";
          const hasSpaceBinding = record.spaceId != null;
          const spaceText =
            hasSpaceBinding
              ? String(spaces.find(s => s.id === record.spaceId)?.name ?? record.spaceId).trim()
              : "";
          const showSpace = hasSpaceBinding && spaceText !== "";
          return (
            <div
              key={record.id}
              className="messages-tokens-mobile-card"
              role="button"
              tabIndex={0}
              onClick={() => setSelected(record.id)}
              onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setSelected(record.id);
                }
              }}
            >
              <div className="messages-tokens-mobile-card-corner" onClick={e => e.stopPropagation()}>
                <Popconfirm
                  title="Подтверждение удаления"
                  description="Вы уверены, что хотите удалить этот канал?"
                  onConfirm={() => handleDelete(record.id)}
                  okText="Удалить"
                  cancelText="Отмена"
                >
                  <Button
                    type="text"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    loading={loading}
                    aria-label="Удалить"
                    className="messages-tokens-mobile-delete-btn"
                  />
                </Popconfirm>
              </div>
              <div className="messages-tokens-mobile-row1">
                <div className="messages-tokens-mobile-active" onClick={e => e.stopPropagation()}>
                  <Switch
                    checked={!!record.active}
                    onChange={checked => handleToggleActive(record, checked)}
                    disabled={loading}
                    size="small"
                  />
                </div>
                {hasName ? <div className="messages-tokens-mobile-name">{String(record.name).trim()}</div> : null}
                <span className="messages-tokens-mobile-vid">{vidLabel(record.vid)}</span>
                <span className="messages-tokens-mobile-provider">{providerLabel(record.provider)}</span>
              </div>
              <div className="messages-tokens-mobile-row2">
                <span>#{record.id}</span>
                <span className="messages-tokens-mobile-sep" aria-hidden>
                  ·
                </span>
                <span>Пор. {record.idx ?? "—"}</span>
                {showSpace ? (
                  <>
                    <span className="messages-tokens-mobile-sep" aria-hidden>
                      ·
                    </span>
                    <span title="Киберменеджер" className="messages-tokens-mobile-space">
                      {spaceText}
                    </span>
                  </>
                ) : null}
                <span className="messages-tokens-mobile-sep" aria-hidden>
                  ·
                </span>
                <span className="messages-tokens-mobile-code">{codeStr || "—"}</span>
              </div>
            </div>
          );
        })}
      </div>
    </Spin>
  );

  return (
    <>
      {isMobile ? (
        <div className="page orders-page--mobile messages-tokens-page--mobile">
          <div className="messages-tokens-mobile-toolbar">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
              disabled={loading}
              aria-label="Добавить"
              className="company-base-header-icon-btn"
            />
            <Button
              type="default"
              icon={<ReloadOutlined />}
              onClick={getData}
              loading={loading}
              aria-label="Обновить"
              className="company-backups-mobile-refresh-btn"
            />
            <Checkbox checked={onlyActive} onChange={e => setOnlyActive(e.target.checked)}>
              Только активные
            </Checkbox>
          </div>
          <div className="messages-tokens-mobile-scroll">{mobileListEl}</div>
        </div>
      ) : (
        <>
          <Space style={{ marginBottom: 16 }} wrap>
            <Button type="primary" onClick={handleAdd} disabled={loading}>
              Добавить
            </Button>
            <Button onClick={getData}>Обновить</Button>
            <Checkbox checked={onlyActive} onChange={e => setOnlyActive(e.target.checked)}>
              Только активные
            </Checkbox>
          </Space>
          {tableEl}
        </>
      )}
      {selected !== null && (
        <MessagesToken onClose={() => setSelected(null)} onSuccess={handleSuccess} id={selected} providers={providers} spaces={spaces} />
      )}
    </>
  );
};

export default MessagesTokens;
